// Display Summary API Service - Real API integration
import { QueueTime } from '../components/CheckpointCard';
import { getCurrentConfig } from '../config';
import { logger } from '../utils/logging/logger';

interface ApiLane {
  type: string;
  status: 'open' | 'close';
  wait_time_minutes: number;
}

interface ApiCheckpoint {
  id: string;
  name: string;
  description: string;
  status: 'open' | 'close';
  wait_time_minutes: number;
  lanes: ApiLane[];
  is_exception: boolean;
}

interface ApiResponse {
  timestamp: string;
  displayswitch: boolean;  // ← ADD THIS LINE
  checkpoints: ApiCheckpoint[];
  structure: string;
  timezone: string;
}

class DisplaySummaryService {
  private readonly API_KEY = process.env.REACT_APP_DISPLAY_API_KEY || 'hdot-display-api-2025-production-key';

  private getApiUrl(): string {
    const config = getCurrentConfig();
    return `${config.displayApiBaseUrl}/display-summary?calling_app=cmaapp`;
  }

  async fetchDisplaySummary(): Promise<ApiResponse> {
    try {
      const apiUrl = this.getApiUrl();
      // Fetching from Display Summary API
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'X-API-Key': this.API_KEY,
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data: ApiResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch display summary:', error);
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to display API. Please check CORS configuration.');
      }
      
      throw error;
    }
  }

  convertLaneToQueueTime(lane: ApiLane, checkpointId: string): QueueTime {
    // Map lane types to our queue types - dynamically based on API response
    const typeMapping: Record<string, QueueTime['type']> = {
      'Standard': 'standard',
      'CLEAR': 'clear', 
      'TSA PreCheck': 'tsa-precheck',
      'Priority': 'priority',
      'Premium': 'premium',
      'Express': 'express'
    };

    // Use the mapped type if available, otherwise default to 'other' for dynamic lanes
    const queueType = typeMapping[lane.type] || 'other';
    
    return {
      id: `${lane.type.toLowerCase().replace(/\s+/g, '-')}-${checkpointId}`,
      name: lane.type, // Use the exact name from API (dynamic)
      type: queueType,
      waitTime: lane.status === 'open' ? lane.wait_time_minutes : null,
      isActive: lane.status === 'open'
    };
  }

  convertCheckpointData(checkpoint: ApiCheckpoint) {
    const checkpointId = parseInt(checkpoint.id, 10);
    
    // Convert all lanes from API response (dynamic number of lanes)
    const queueTimes = checkpoint.lanes.map(lane => 
      this.convertLaneToQueueTime(lane, checkpoint.id)
    );

    return {
      checkpointId,
      queueTimes,
      isOpen: checkpoint.status === 'open',
      isException: checkpoint.is_exception,
      totalLanes: checkpoint.lanes.length
    };
  }

  async getAllQueueTimesFromAPI() {
    try {
      const data = await this.fetchDisplaySummary();
      
      const queueTimes: Record<number, QueueTime[]> = {};
      const checkpointStatuses: Record<number, { isOpen: boolean; isException: boolean; totalLanes: number }> = {};
      
      data.checkpoints.forEach(checkpoint => {
        const converted = this.convertCheckpointData(checkpoint);
        queueTimes[converted.checkpointId] = converted.queueTimes;
        checkpointStatuses[converted.checkpointId] = {
          isOpen: converted.isOpen,
          isException: converted.isException,
          totalLanes: converted.totalLanes
        };
      });

      return {
        queueTimes,
        checkpointStatuses,
        lastUpdated: data.timestamp,
        displaySwitch: data.displayswitch
      };
    } catch (error) {
      console.error('Failed to get queue times from API:', error);
      throw error;
    }
  }

  async checkAPIHealth(): Promise<boolean> {
    try {
      const apiUrl = this.getApiUrl();
      const response = await fetch(apiUrl, {
        method: 'HEAD',
        headers: {
          'X-API-Key': this.API_KEY,
        },
        mode: 'cors',
      });
      return response.ok;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }
}

export const displaySummaryService = new DisplaySummaryService();